import classnames from "classnames";
import compact from "lodash/compact";

import styles from "./withCodesInput.module.scss";

const KEY_CODES = {
  BACKSPACE: 8,
};

const GUTTER_WIDTH = "10px";

function withCodesInput(React, PropTypes) {
  class CodeInput extends React.Component {
    componentDidMount() {
      const { focusOnMount } = this.props;

      if (focusOnMount) {
        this.ref.focus();
      }

      this.ref.addEventListener("focus", this.onFocus);
      this.ref.addEventListener("keydown", this.onKeyDown);
      global.document.addEventListener("paste", this.onPaste);
    }

    componentWillUnmount() {
      this.ref.removeEventListener("focus", this.onFocus);
      this.ref.removeEventListener("keydown", this.onKeyDown);
      global.document.removeEventListener("paste", this.onPaste);
    }

    onFocus = () => {
      // Always focus cursor at the end
      this.ref.setSelectionRange(0, this.ref.value.length);
    };

    onKeyDown = e => {
      if (e.keyCode === KEY_CODES.BACKSPACE) {
        e.preventDefault();
        this.props.onDelete();
      }
    };

    onPaste = e => {
      e.stopPropagation();
      e.preventDefault();
      this.props.onPaste(e);
    };

    onRef = el => {
      const { onRef } = this.props;
      onRef(el);
      this.ref = el;
    };

    render() {
      const { length, focusOnMount, onRef, onDelete, ...inputProps } =
        this.props;
      return (
        <input
          className={classnames("code-input", styles["code-input"])}
          style={{ width: `calc(100% / ${length} - ${GUTTER_WIDTH})` }}
          ref={this.onRef}
          type="text"
          pattern="[0-9]*"
          {...inputProps}
        />
      );
    }
  }

  CodeInput.propTypes = {
    focusOnMount: PropTypes.bool.isRequired,
    onRef: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  };

  CodeInput.defaultProps = {};

  class CodesInput extends React.PureComponent {
    constructor(props) {
      super(props);

      const { length } = this.props;

      const range = Array.from(Array(length));
      this.codeInputKeys = [...range].map((_, i) => i);
      this.codeInputRefs = [...range];
      this.state = {
        codes: range.map(() => ""),
      };
    }

    getValue = () => {
      const { codes } = this.state;

      return compact(codes).join("");
    };

    isFilled = () => {
      const { length } = this.props;

      return this.getValue().length === length;
    };

    onCodeInputRef = (index, el) => {
      this.codeInputRefs[index] = el;
    };

    onProceedIndex = index => {
      const { length } = this.props;
      const nextIndexToFocus = index % length;
      this.codeInputRefs[nextIndexToFocus].focus();
    };

    onCodeChange = (index, e) => {
      const value = e.target.value;

      const { length, onCodeEntered } = this.props;
      let { codes } = this.state;

      codes = [...codes];
      codes[index] = value[value.length - 1];

      this.setState({ codes }, () => {
        if (this.isFilled()) {
          // Update upstream callback on next tick so that
          // the UI gets a chance to update, in case this
          // component is unmounted upon completion of code
          // entry.
          global.setTimeout(() => onCodeEntered(this.getValue()), 500);
        }

        this.onProceedIndex(index + 1);
      });
    };

    onCodeDelete = index => {
      // If focus is on the first one, allow delete of first value.
      if (index === 0) {
        index = 1;
      }

      let { codes } = this.state;

      codes = [...codes];
      codes[index - 1] = "";

      this.setState({ codes }, () => {
        if (index === 0) return;
        this.onProceedIndex(index - 1);
      });
    };

    onCodePaste = (index, e) => {
      if (index > 0) return;
      const { length, onCodeEntered } = this.props;
      const paste = (e.clipboardData || window.clipboardData).getData("Text");
      let { codes } = this.state;

      codes = [...codes];
      for (let i = 0; i < paste.length; i++) {
        codes[i] = paste[i];
      }

      this.setState({ codes }, () => {
        if (this.isFilled()) {
          // Update upstream callback on next tick so that
          // the UI gets a chance to update, in case this
          // component is unmounted upon completion of code
          // entry.
          global.setTimeout(() => onCodeEntered(this.getValue()), 350);
        }

        this.onProceedIndex(paste.length);
      });
    };

    render() {
      const { codes } = this.state;
      return (
        <div className={styles.container}>
          {codes.map((code, index) => (
            <CodeInput
              key={this.codeInputKeys[index]}
              length={this.props.length}
              focusOnMount={index === 0}
              onRef={el => this.onCodeInputRef(index, el)}
              value={code}
              onChange={e => this.onCodeChange(index, e)}
              onDelete={e => this.onCodeDelete(index)}
              onPaste={e => this.onCodePaste(index, e)}
            />
          ))}
        </div>
      );
    }
  }

  CodesInput.propTypes = {
    length: PropTypes.number.isRequired,
    onCodeEntered: PropTypes.func.isRequired,
  };

  CodesInput.defaultProps = {};

  return CodesInput;
}

export default withCodesInput;
